
/**
 * UFB Status mapping
 */

//State/Substate combinations mapping
const UFB_STATE_MAPPING = {
    // Active states - equivalent to mobile Status 4
    active_combinations: [
        { state: 'active', substate: null },
        { state: 'suspended', substate: null }, //treat suspended as active
    ],
    
    // Pending states - equivalent to mobile Status < 4
    pending_combinations: [
        { state: 'created', substate: null },
        { state: 'acknowledged', substate: null },

    ],
    
    // Cancelled/Failed states - equivalent to mobile Status > 4
    cancelled_combinations: [
        { state: 'cancelled', substate: null },
    ]
};

const UFB_SUBSTATE_TEXT_MAPPING = {
    'RequiresSiteVisitScopeBooking': 'Site visit required',
    'RequiresNewRFSTime': 'RFS time required',
    'RequiresSiteVisitInstallBooking': 'Site visit required',
    'RequiresSiteVisitCSEBooking': 'Site visit required',
    'RequiresSiteVisitInstallCSEBooking': 'Site visit required',
    'RequiresSiteVisitChangeONTBooking': 'Site visit required',
    'RequiresSiteVisitReplaceONTBooking': 'Site visit required',
    'PendingCancellation': 'Pending cancellation',
    'COMPLETED': 'Active',
}

export function getUFBSubstateText(substateKey, state = null) {

    if (state && state.toLowerCase() == 'active') return 'Active'

    return UFB_SUBSTATE_TEXT_MAPPING[substateKey] || substateKey ||state || ''
}

/**
 * Determines UFB status category from State/Substate combination
 * @param {Object} ufbProduct - UFB product data with State and Substate
 * @returns {string} - Status category: 'active', 'pending', 'suspended', 'cancelled', 'unknown'
 */
export function getUFBStatusCategory(ufbProduct) {
    if (!ufbProduct || ufbProduct.DeletedAtNanos) {
        return null; // Skip deleted/invalid products
    }
    
    const state = (ufbProduct.StateDescription || '').toLowerCase()
    const substate = ufbProduct.Substate;
    
    // Check each category
    for (const [category, combinations] of Object.entries(UFB_STATE_MAPPING)) {
        const categoryName = category.replace('_combinations', '');
        
        const matchFound = combinations.some(combo => {
            const stateMatch = combo.state === state;
            const substateMatch = combo.substate === null || combo.substate === substate;
            return stateMatch && substateMatch;
        });
        
        if (matchFound) {
            return categoryName;
        }
    }
    
    return 'unknown';
}

/**
 * Determines ServiceType for GetCustomerByUuid (account-level)
 * Includes BillingReference validation as per business requirements
 * @param {Object} ufbProduct - UFB product data
 * @returns {string} - ServiceType compatible with mobile products
 */
export function getUFBAccountServiceType(ufbProduct) {
    const statusCategory = getUFBStatusCategory(ufbProduct);
    
    if (!statusCategory) {
        return null; // Skip invalid products
    }
    
    // BillingReference validation (critical business requirement)
    const hasBillingReference = ufbProduct?.BillingReference?.startsWith('postpay');
    
    switch (statusCategory) {
        case 'active':
            return hasBillingReference ? 'ufb' : 'Not Activated';
            
        case 'suspended':
            return hasBillingReference ? 'ufb_suspended' : 'Not Activated';
            
        case 'pending':
            return 'Not Activated';
            
        case 'cancelled':
        case 'unknown':
            return 'Not Active';
            
        default:
            return 'Not Active';
    }
}

/**
 * Determines numeric Status for unified product system (product-level)
 * Maps UFB State/Substate to numeric values compatible with mobile products
 * @param {Object} ufbProduct - UFB product data
 * @returns {number} - Numeric status (1-5) compatible with mobile Status field
 */
export function getUFBNumericStatus(ufbProduct) {
    const statusCategory = getUFBStatusCategory(ufbProduct);
    
    switch (statusCategory) {
        case 'active':
            return 4; // Status 4 (active)
        case 'pending':
            return 2; // Status 2 (pre-activated)
        case 'suspended':
            return 3; // Status 3 (suspended)
        case 'cancelled':
            return 5; // Status 5+ (cancelled)
        case 'unknown':
        default:
            return 1; // Status 1 (unknown/initial)
    }
}

/**
 * Determines detailed ServiceType for product-level processing
 * Used in firebase_auth_plan.js for product categorization
 * @param {Object} ufbProduct - UFB product data
 * @returns {string} - Detailed ServiceType for product filtering
 */
export function getUFBProductServiceType(ufbProduct) {
    const statusCategory = getUFBStatusCategory(ufbProduct);
    const hasBillingReference = ufbProduct?.BillingReference?.startsWith('postpay');
    const serviceType = ufbProduct?.BillingReference?.startsWith('postpay') ? 'postpay' : 'Not Activated';
    
    switch (statusCategory) {
        case 'active':
        case 'pending':
            return serviceType;
        case 'suspended':
        case 'cancelled':
        case 'unknown':
        default:
            return 'Not Active';
    }
}

export { UFB_STATE_MAPPING };